import React, { useState } from 'react';
import { X } from 'lucide-react';
// Mismo formato que el resto de modales de datos del cliente: se reutilizan las
// clases `sm-*` del modal de envio (el CSS de este proyecto es global).
import '../ShippingModal/ShippingModal.css';
import { DerTraveler } from '../../types/der.types';
import './DerModal.css';

interface DerModalProps {
  clientName: string;
  initial?: DerTraveler | null;
  onClose: () => void;
  onConfirm: (traveler: DerTraveler) => void;
}

const EMPTY: DerTraveler = {
  passport: '',
  fullName: '',
  birthDate: '',
  residenceCountry: '',
  departureDate: '',
  email: '',
  phone: '',
  transportNumber: ''
};

const LOOKS_LIKE_EMAIL = /^\S+@\S+\.\S+$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Los EEE miembros no pueden ser pais de residencia: el DER es solo para residentes fuera de la UE. */
const EU = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];

/**
 * Pide los datos del viajero para solicitar el DER (devolucion de IVA, sistema
 * DIVA de la AEAT). Nada se guarda en la ficha del cliente.
 */
export function DerModal({ clientName, initial, onClose, onConfirm }: DerModalProps) {
  const [t, setT] = useState<DerTraveler>(initial || EMPTY);
  const set = (patch: Partial<DerTraveler>) => setT(prev => ({ ...prev, ...patch }));

  const country = t.residenceCountry.trim().toUpperCase();
  const error =
    !t.passport.trim() ? 'Falta el pasaporte.'
    : !t.fullName.trim() ? 'Falta el nombre y apellidos.'
    : !DATE.test(t.birthDate) ? 'Falta la fecha de nacimiento.'
    : !/^[A-Z]{2}$/.test(country) ? 'El país de residencia son 2 letras (ej. US).'
    : EU.indexOf(country) !== -1 ? 'El DER solo es para residentes fuera de la UE.'
    : !DATE.test(t.departureDate) ? 'Falta la fecha de salida de la UE.'
    : t.email && !LOOKS_LIKE_EMAIL.test(t.email.trim()) ? 'El email no es válido.'
    : '';

  const field = (label: string, node: React.ReactNode) => (
    <div className="sm-field">
      <label className="sm-label">{label}</label>
      {node}
    </div>
  );

  return (
    <div className="sm-overlay der-overlay">
      <div className="sm-modal">
        <div className="sm-header">
          <div>
            <h3 className="sm-title">Solicitar DER (devolución de IVA)</h3>
            <p className="sm-subtitle">Cliente: {clientName}</p>
          </div>
          <button onClick={onClose} className="sm-close" title="Cerrar">
            <X style={{ width: '1.5rem', height: '1.5rem' }} />
          </button>
        </div>

        <div className="sm-form">
          <div className="sm-fields">
            {field('Pasaporte *', <input autoFocus className="sm-input" maxLength={17} value={t.passport} onChange={e => set({ passport: e.target.value })} />)}
            {field('Nombre y apellidos *', <input className="sm-input" maxLength={70} value={t.fullName} onChange={e => set({ fullName: e.target.value })} />)}
            {field('Fecha de nacimiento *', <input type="date" className="sm-input" value={t.birthDate} onChange={e => set({ birthDate: e.target.value })} />)}
            {field('País de residencia * (2 letras)', <input className="sm-input" maxLength={2} placeholder="US" value={t.residenceCountry} onChange={e => set({ residenceCountry: e.target.value.toUpperCase() })} />)}
            {field('Fecha de salida de la UE *', <input type="date" className="sm-input" value={t.departureDate} onChange={e => set({ departureDate: e.target.value })} />)}
            {field('Nº de vuelo o barco', <input className="sm-input" maxLength={35} value={t.transportNumber || ''} onChange={e => set({ transportNumber: e.target.value })} />)}
            {field('Email del viajero', <input type="email" className="sm-input" value={t.email || ''} onChange={e => set({ email: e.target.value })} />)}
            {field('Teléfono', <input className="sm-input" value={t.phone || ''} onChange={e => set({ phone: e.target.value })} />)}
          </div>

          {error && <p className="sm-subtitle" style={{ color: '#EF4444' }}>{error}</p>}

          <div className="sm-actions">
            <button onClick={onClose} className="sm-btn-back">CANCELAR</button>
            <button
              disabled={!!error}
              onClick={() => onConfirm({ ...t, passport: t.passport.trim(), fullName: t.fullName.trim(), residenceCountry: country })}
              className="sm-btn-confirm"
            >
              SOLICITAR AL COBRAR
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
